import { useEffect, useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { api } from '@/utils/api';
import MarketSelect from '@/components/base/MarketSelect';
import { africanMarkets } from '@/mocks/markets';
import { currenciesForMarkets } from '@/utils/markets';
import type { Campaign } from '@/types/portal';
import { contentTypeOptions } from '../../new/types';

/** CpaasCampaign::PAYMENT_TERMS (days). */
const PAYMENT_TERMS = [30, 45, 60, 90];

export interface CampaignEdits {
  name: string;
  description: string;
  deliverables: string;
  terms: string;
  budgetPerCreator: string;
  currency: string;
  markets: string[];
  contentTypes?: string[];
  startDate: string;
  endDate: string;
  linkExpiresOn: string;
  contractDate: string;
  paymentTerms: string;
}

interface EditCampaignModalProps {
  open: boolean;
  campaign: Campaign;
  saving: boolean;
  error: string;
  onClose: () => void;
  /** Only the fields that changed, so untouched values (e.g. an already-passed link expiry) aren't re-validated. */
  onSave: (changes: Partial<CampaignEdits>) => void;
}

const inputClass =
  'h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none';
const areaClass =
  'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none';

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
        {label}
      </label>
      {children}
    </div>
  );
}

function fromCampaign(campaign: Campaign): CampaignEdits {
  return {
    name: campaign.name,
    description: campaign.description,
    deliverables: campaign.deliverables ?? '',
    terms: campaign.terms ?? '',
    budgetPerCreator: campaign.budgetPerCreator ? String(campaign.budgetPerCreator) : '',
    currency: campaign.currency,
    markets: campaign.markets,
    contentTypes: campaign.contentTypes,
    startDate: campaign.startDate,
    endDate: campaign.endDate,
    linkExpiresOn: campaign.linkExpiresOn ?? '',
    contractDate: campaign.contractDate ?? '',
    paymentTerms: campaign.paymentTerms ? String(campaign.paymentTerms) : '',
  };
}

/** The live portal's Edit campaign form (campaigns/edit.blade.php), as a dialog. */
export default function EditCampaignModal({ open, campaign, saving, error, onClose, onSave }: EditCampaignModalProps) {
  const [initial, setInitial] = useState<CampaignEdits>(() => fromCampaign(campaign));
  const [form, setForm] = useState<CampaignEdits>(initial);
  // The live Edit page's "Generate with AI" (POST campaigns/{id}/generate-terms): fills the box, saved with Save.
  const [generating, setGenerating] = useState(false);
  const [termsError, setTermsError] = useState('');
  const generateTerms = async () => {
    setGenerating(true);
    setTermsError('');
    try {
      const { terms } = await api<{ terms: string }>(`/campaigns/${campaign.id}/generate-terms`, { method: 'POST' });
      set({ terms });
    } catch (error) {
      setTermsError(error instanceof Error ? error.message : 'Could not generate terms. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const start = fromCampaign(campaign);
    setInitial(start);
    setForm(start);
  }, [open, campaign]);

  const changes = useMemo(
    () =>
      Object.fromEntries(
        (Object.keys(form) as (keyof CampaignEdits)[])
          .filter((key) => JSON.stringify(form[key]) !== JSON.stringify(initial[key]))
          .map((key) => [key, typeof form[key] === 'string' ? (form[key] as string).trim() : form[key]]),
      ) as Partial<CampaignEdits>,
    [form, initial],
  );

  const set = (patch: Partial<CampaignEdits>) => setForm((current) => ({ ...current, ...patch }));

  const currencies = useMemo(() => {
    const preferred = currenciesForMarkets(form.markets);
    const all = africanMarkets.map((market) => market.currency);
    return [...new Set([form.currency, ...preferred, ...all].filter(Boolean))];
  }, [form.markets, form.currency]);

  const datesInvalid = Boolean(form.startDate && form.endDate && form.endDate < form.startDate);
  const valid = form.name.trim().length > 0 && !datesInvalid && Object.keys(changes).length > 0;

  const toggleType = (label: string) =>
    set({
      contentTypes: (form.contentTypes ?? []).includes(label)
        ? (form.contentTypes ?? []).filter((entry) => entry !== label)
        : [...(form.contentTypes ?? []), label],
    });

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon="ri-edit-line"
      title="Edit campaign"
      subtitle="Changes show to creators who open the brief from now on"
      widthClassName="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button icon="ri-check-line" disabled={!valid || saving} onClick={() => onSave(changes)}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <p role="alert" className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5" />
            {error}
          </p>
        ) : null}

        <Field id="edit-name" label="Campaign name">
          <input id="edit-name" className={inputClass} value={form.name} maxLength={255} onChange={(e) => set({ name: e.target.value })} />
        </Field>

        <Field id="edit-description" label="Campaign description">
          <textarea id="edit-description" className={areaClass} rows={4} value={form.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>

        <Field id="edit-deliverables" label="Deliverables">
          <textarea id="edit-deliverables" className={areaClass} rows={3} value={form.deliverables} onChange={(e) => set({ deliverables: e.target.value })} />
        </Field>

        {form.contentTypes ? (
          <div>
            <p className="mb-1.5 text-xs font-label font-medium text-foreground-700">Content types</p>
            <div className="flex flex-wrap gap-2">
              {contentTypeOptions.map((type) => {
                const on = form.contentTypes?.includes(type.label);
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => toggleType(type.label)}
                    aria-pressed={on}
                    className={`cursor-pointer rounded-full border px-3 py-1 text-xs font-label transition-colors ${
                      on
                        ? 'border-primary-400 bg-primary-50 text-primary-800'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <Field id="edit-markets" label="Markets">
          <MarketSelect id="edit-markets" value={form.markets} onChange={(markets) => set({ markets })} />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field id="edit-budget" label="Budget per creator">
            <input
              id="edit-budget"
              type="number"
              min={0}
              step="0.01"
              className={inputClass}
              value={form.budgetPerCreator}
              onChange={(e) => set({ budgetPerCreator: e.target.value })}
            />
          </Field>
          <Field id="edit-currency" label="Currency">
            <select id="edit-currency" className={inputClass} value={form.currency} onChange={(e) => set({ currency: e.target.value })}>
              {currencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </Field>
          <Field id="edit-start" label="Start date">
            <input id="edit-start" type="date" className={inputClass} value={form.startDate} onChange={(e) => set({ startDate: e.target.value })} />
          </Field>
          <Field id="edit-end" label="End date">
            <input id="edit-end" type="date" className={inputClass} value={form.endDate} onChange={(e) => set({ endDate: e.target.value })} />
          </Field>
          <Field id="edit-contract-date" label="Contract date">
            <input id="edit-contract-date" type="date" className={inputClass} value={form.contractDate} onChange={(e) => set({ contractDate: e.target.value })} />
          </Field>
          <Field id="edit-payment-terms" label="Payment terms">
            <select id="edit-payment-terms" className={inputClass} value={form.paymentTerms} onChange={(e) => set({ paymentTerms: e.target.value })}>
              <option value="">Not set</option>
              {PAYMENT_TERMS.map((days) => (
                <option key={days} value={days}>
                  {days} days after the contract date
                </option>
              ))}
            </select>
          </Field>
          <Field id="edit-expiry" label="Onboarding link expires">
            <input id="edit-expiry" type="date" className={inputClass} value={form.linkExpiresOn} onChange={(e) => set({ linkExpiresOn: e.target.value })} />
          </Field>
        </div>
        {datesInvalid ? (
          <p className="text-xs text-primary-700">The end date must be on or after the start date.</p>
        ) : null}

        <div>
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <label htmlFor="edit-terms" className="block text-xs font-label font-medium text-foreground-700">
              Contract terms
            </label>
            <Button size="sm" variant="ghost" icon="ri-sparkling-2-line" disabled={generating} onClick={() => void generateTerms()}>
              {generating ? 'Generating…' : 'Generate with AI'}
            </Button>
          </div>
          <textarea id="edit-terms" className={areaClass} rows={4} value={form.terms} onChange={(e) => set({ terms: e.target.value })} />
          {termsError ? (
            <p role="alert" className="mt-1 text-xs text-primary-700">{termsError}</p>
          ) : (
            <p className="mt-1 text-[11px] text-foreground-400">
              AI drafts terms from the saved campaign; check them, then Save.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
